import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import SelectProfession from "components/SelectProfession";
import SelectState from "components/SelectState";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { useCallback, useMemo } from "react";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  IQualification,
  IQualificationApproval,
  SaveQualificationApprovalRequest,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { ValidatePartialCredit } from "types/partialCreditValidator";
import translateError from "utils/translateError";

const { Form } = withTypes<SaveQualificationApprovalRequest>();

type QualificationApprovalFormProps = {
  qualification: IQualification;
  qualificationApproval?: IQualificationApproval | null;
  title: string;
  onCancel?: () => void;
  onSubmit: () => void;
};

const QualificationApprovalForm = ({
  qualification,
  qualificationApproval,
  title,
  onCancel,
  onSubmit,
}: QualificationApprovalFormProps) => {
  const { formatMessage: t } = useIntl();
  const { qualificationService } = useGlobalContext();
  const init = useMemo(
    () => ({
      qualificationId: qualification.id,
      id: qualificationApproval?.id,
      stateId: qualificationApproval?.stateId,
      professionId: qualificationApproval?.professionId,
      minimumCredit: qualificationApproval?.minimumCredit,
      partialCreditIncrement: qualificationApproval?.partialCreditIncrement,
    }),
    [qualification.id, qualificationApproval],
  );

  const { organizationId } = contextStore.useState();

  const handleSubmit = useCallback(
    async (values: SaveQualificationApprovalRequest) => {
      try {
        values.organizationId = organizationId;
        await qualificationService.saveQualificationApproval(values);
        onSubmit();
      } catch (error) {
        console.log(error);
        return { [FORM_ERROR]: translateError(error, t) };
      }
    },
    [onSubmit, qualificationService, organizationId, t],
  );

  const validateForm = (values: SaveQualificationApprovalRequest) => {
    return ValidatePartialCredit(t, values);
  };

  return (
    <Form
      onSubmit={handleSubmit}
      validate={validateForm}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit} noValidate>
          <DialogTitle id="form-dialog-title">{title}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="stateId">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={meta.error && meta.submitFailed}
                    >
                      <SelectState
                        {...input}
                        label={t({ id: "locationFormStateLabel" })}
                        textFieldProps={{
                          error: meta.error && meta.submitFailed,
                        }}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              <Field name="professionId">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={meta.error && meta.submitFailed}
                    >
                      <SelectProfession
                        {...input}
                        label={t({
                          id: "professionLabel",
                        })}
                        textFieldProps={{
                          error: meta.error && meta.submitFailed,
                        }}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText error>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              <Field name="minimumCredit" type="number">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      type="number"
                      label={t({ id: "minimumCreditMessage" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed}
                      data-cy="minimumCreditInput"
                      {...input}
                    />
                    {meta.error && meta.submitFailed && (
                      <FormHelperText error>{meta.error}</FormHelperText>
                    )}
                  </Grid>
                )}
              </Field>

              <Field name="partialCreditIncrement" type="number">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      type="number"
                      label={t({ id: "partialCreditIncrementMessage" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed}
                      data-cy="partialCreditIncrementInput"
                      {...input}
                    />
                    {meta.error && meta.submitFailed && (
                      <FormHelperText error>{meta.error}</FormHelperText>
                    )}
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Grid item xs={12}>
                  <Alert severity="error">{submitError}</Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            {submitting && <CircularProgress size={24} />}
            <Button
              disabled={submitting}
              onClick={onCancel}
              data-cy="cancelButton"
            >
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={submitting}
              data-cy="saveButton"
            >
              {t({ id: "saveButton" })}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default QualificationApprovalForm;
